import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { contractTemplatesApi } from '../lib/api';
import { toast } from 'sonner';
import {
  FileText, Plus, Save, Trash2, Copy, Loader2,
} from 'lucide-react';

export default function ContractTemplates() {
  const [templates, setTemplates] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [draftBody, setDraftBody] = useState('');
  const [draftName, setDraftName] = useState('');
  const [draftType, setDraftType] = useState('subdivide');
  const [draftDefault, setDraftDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('custom');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contractTemplatesApi.list();
      setTemplates(res.data?.templates || []);
      setMergeFields(res.data?.merge_fields || []);
      // Auto-select first template if none active
      if (!activeId && res.data?.templates?.length) {
        const first = res.data.templates[0];
        setActiveId(first.id);
        setDraftName(first.name);
        setDraftBody(first.body || '');
        setDraftType(first.deal_type || 'custom');
        setDraftDefault(first.is_default_for_type || false);
      }
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectTemplate = (id) => {
    const t = templates.find(x => x.id === id);
    if (!t) return;
    setActiveId(id);
    setDraftName(t.name);
    setDraftBody(t.body || '');
    setDraftType(t.deal_type || 'custom');
    setDraftDefault(t.is_default_for_type || false);
  };

  const saveTemplate = async () => {
    if (!activeId) return;
    setSaving(true);
    try {
      const res = await contractTemplatesApi.update(activeId, {
        name: draftName,
        body: draftBody,
        deal_type: draftType,
        is_default_for_type: draftDefault,
      });
      toast.success('Template saved');
      setTemplates(prev => prev.map(t => t.id === activeId ? res.data : t));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const createTemplate = async () => {
    if (!newName.trim()) {
      toast.error('Template name is required');
      return;
    }
    try {
      const res = await contractTemplatesApi.create({
        name: newName.trim(),
        deal_type: newType,
        body: '',
      });
      toast.success('Template created');
      setShowNewDialog(false);
      setNewName('');
      setNewType('custom');
      await load();
      setActiveId(res.data.id);
      setDraftName(res.data.name);
      setDraftBody(res.data.body || '');
      setDraftType(res.data.deal_type);
      setDraftDefault(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const deleteTemplate = async () => {
    if (!activeId) return;
    if (!window.confirm(`Delete template "${draftName}"? This cannot be undone.`)) return;
    try {
      await contractTemplatesApi.remove(activeId);
      toast.success('Template deleted');
      setActiveId(null);
      setDraftBody('');
      setDraftName('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const insertField = (key) => {
    // Insert at the end of the body — simplest approach. For caret-aware
    // insertion we'd need a textarea ref + selectionStart manipulation.
    setDraftBody(prev => prev + key);
    // Copy to clipboard too for convenience
    if (navigator.clipboard) navigator.clipboard.writeText(key);
    toast.success(`Inserted ${key} (also copied to clipboard)`);
  };

  const typeBadgeCls = (t) =>
    t === 'subdivide' ? 'border-blue-300 text-blue-700 bg-blue-50' :
    t === 'flip' ? 'border-orange-300 text-orange-700 bg-orange-50' :
    'border-foreground/30 text-foreground/70 bg-muted/40';

  return (
    <div className="container mx-auto px-4 sm:px-6 py-6 space-y-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#C68E5D]" />
            Contract Templates
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Master JV agreement templates. When you open a committed deal in Partners → Promoted Deals,
            the appropriate template is auto-populated with the deal's data and you can tweak it before downloading the PDF.
          </p>
        </div>
        <Button
          onClick={() => setShowNewDialog(true)}
          className="bg-[#C68E5D] hover:bg-[#C68E5D]/90 text-white rounded-sm gap-1.5"
          data-testid="new-template-btn"
        >
          <Plus className="w-4 h-4" />
          New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Sidebar: template list */}
        <Card className="rounded-sm h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">Templates</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {loading ? (
              <div className="text-xs text-muted-foreground p-3">
                <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading...
              </div>
            ) : templates.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3">No templates yet.</p>
            ) : (
              <ul className="space-y-1">
                {templates.map(t => (
                  <li key={t.id}>
                    <button
                      onClick={() => selectTemplate(t.id)}
                      className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                        activeId === t.id ? 'bg-[#C68E5D]/15 text-foreground' : 'hover:bg-muted/50'
                      }`}
                      data-testid={`template-${t.id}`}
                    >
                      <div className="font-medium truncate">{t.name}</div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Badge variant="outline" className={`text-[9px] uppercase tracking-wide ${typeBadgeCls(t.deal_type)}`}>
                          {t.deal_type}
                        </Badge>
                        {t.is_default_for_type && (
                          <span className="text-[9px] text-emerald-600 font-medium">DEFAULT</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Editor */}
        <div className="space-y-4">
          {!activeId ? (
            <Card className="rounded-sm">
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Select a template on the left, or click "New Template" to create one.
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="rounded-sm">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Template Name</Label>
                      <Input
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        className="rounded-sm"
                        data-testid="template-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Deal Type</Label>
                      <Select value={draftType} onValueChange={setDraftType}>
                        <SelectTrigger data-testid="template-type-select" className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="subdivide">Subdivide</SelectItem>
                          <SelectItem value="flip">Flip</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draftDefault}
                      onChange={(e) => setDraftDefault(e.target.checked)}
                      data-testid="template-default-checkbox"
                    />
                    Use as the default template for {draftType} deals
                  </label>
                </CardContent>
              </Card>

              {/* Body editor */}
              <Card className="rounded-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-sm">Contract Body</CardTitle>
                    <p className="text-[11px] text-muted-foreground">
                      Use <code className="bg-muted px-1 rounded-sm">{'{{merge_field}}'}</code> placeholders. Click any field on the right to copy & insert.
                    </p>
                  </div>
                </CardHeader>
                <CardContent>
                  <textarea
                    value={draftBody}
                    onChange={(e) => setDraftBody(e.target.value)}
                    className="w-full font-mono text-[13px] border rounded-sm p-3 bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                    rows={28}
                    spellCheck={false}
                    data-testid="template-body-textarea"
                    placeholder="Paste your contract template here. Use {{investor_name}}, {{property_county}}, etc. for fields that should be auto-populated when the contract is opened for a specific deal."
                  />
                </CardContent>
              </Card>

              {/* Action bar */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={saveTemplate}
                  disabled={saving}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm gap-1.5"
                  data-testid="save-template-btn"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save Template
                </Button>
                <Button
                  variant="outline"
                  onClick={deleteTemplate}
                  className="text-xs rounded-sm gap-1.5 text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="delete-template-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </Button>
                <p className="text-[11px] text-muted-foreground ml-auto">
                  Changes only affect future "Open Contract" actions. Saved drafts on existing deals are preserved.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Merge fields reference */}
      {activeId && (
        <Card className="rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Merge Fields Reference</CardTitle>
            <p className="text-[11px] text-muted-foreground">
              Click any field to copy it to your clipboard and insert at the end of the template body.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {mergeFields.map(f => (
                <button
                  key={f.key}
                  onClick={() => insertField(f.key)}
                  className="text-left px-2 py-1.5 rounded-sm border bg-muted/20 hover:bg-muted/50 hover:border-[#C68E5D]/40 transition-colors group"
                  data-testid={`merge-field-${f.key.replace(/[{}]/g, '')}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Copy className="w-3 h-3 opacity-40 group-hover:opacity-80" />
                    <code className="text-[11px] font-mono text-[#C68E5D] truncate">{f.key}</code>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                    {f.label} → <span className="opacity-70">{f.example}</span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* New template dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-md" data-testid="new-template-dialog">
          <DialogHeader>
            <DialogTitle>New Contract Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Subdivide JV Agreement v3"
                className="rounded-sm"
                data-testid="new-template-name"
              />
            </div>
            <div>
              <Label className="text-xs">Deal Type</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger data-testid="new-template-type" className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subdivide">Subdivide</SelectItem>
                  <SelectItem value="flip">Flip</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={createTemplate} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm" data-testid="create-template-btn">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
